import { Button, Radio, Space } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { keycloak } from "../../..";
import { useAppSelector } from "../../../app/store";
import SidebarLayout from "../../../layouts/SidebarLayout";

import { DatePicker, Form, Input } from "antd";

import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect } from "react";
import DateIcon from "../../../assets/icons/DateIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import AssignProductModal from "../../../components/Businesses/AssignProductModal";
import CardTitle from "../../../components/common/CardTitle";
import CustomCard from "../../../components/common/CustomCard";
import EmployeeFileUpload from "../../../components/Employees/EmployeeFileUpload";
import Loader from "../../../components/ui/Loader";
import { dateFormat, mailRegex } from "../../../constants/constants";
import {
  useCreateCompanyMutation,
  useGetSingleCompanyQuery,
  useUpdateCompanyMutation,
} from "../../../features/portal/companyOnboard/companyOnboardApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { toast } from "../../../lib/toast";
import { isValidTLD } from "../../../utils/utils";

const backLink = "/admin/businesses/business-list";

export default function AddNewBusiness() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { role } = useAppSelector((state) => state.auth);
  const isSuperAdmin =
    role === "SUPER_ADMIN" ||
    keycloak?.realmAccess?.roles?.includes("super_admin");
  const { open, handleClose } = useModal();
  const [createCompany, { isLoading, isError, error, isSuccess }] =
    useCreateCompanyMutation();
  useError(isError, error, "Company create fail");

  const {
    isError: isDetailsError,
    error: detailsError,
    isLoading: isDetailsLoading,
    data: dataDetails,
  } = useGetSingleCompanyQuery(id || "", {
    skip: !id,
  });
  useError(isDetailsError, detailsError, "Get single company fail");
  const detailsData = dataDetails?.data;

  const [
    updateCompany,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateCompanyMutation();
  useError(isUpdateError, updateError, "Company update fail");

  // console.log(detailsData);

  const onFinish = (values: any) => {
    const newValues = {
      ...values,
      dynamicKeys:
        !values?.dynamicKeys ||
        values.dynamicKeys.length === 0 ||
        values.dynamicKeys.every((item: any) =>
          Object.values(item).every((v) => v === undefined || v === null)
        )
          ? null
          : values.dynamicKeys.map((item: any) => ({
              ...item,
              dynamic_id:
                item.dynamic_id !== undefined &&
                item.dynamic_id !== null &&
                item.dynamic_id !== ""
                  ? Number(item.dynamic_id)
                  : null,
            })),
    };

    if (id && !isSuperAdmin) {
      delete newValues.business_email;
    }

    if (id) {
      updateCompany({
        id: id,
        data: newValues,
      });
    } else {
      createCompany(newValues);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Company created successfully");
      navigate(backLink);
    }

    if (isUpdateSuccess) {
      toast("success", "Company updated successfully");
      navigate(backLink);
    }
  }, [isSuccess, isUpdateSuccess, navigate]);

  // useEffect(() => {
  //   if (Object.keys(detailsData || {})) {
  //     form.setFieldsValue({});
  //   }
  // }, []);

  // Update form values when detailsData is available
  useEffect(() => {
    if (detailsData) {
      form.setFieldsValue({
        logo: detailsData.logo,
        company_name: detailsData.company_name,
        is_published:
          typeof detailsData?.is_published === "number"
            ? detailsData.is_published
            : typeof detailsData?.is_published === "boolean"
            ? detailsData.is_published
              ? 1
              : 0
            : detailsData?.status?.toLowerCase() === "unpublished"
            ? 0
            : 1,
        business_email: detailsData.business_email,
        mobile_number: detailsData.mobile_number,
        location: detailsData.location,
        address: detailsData.address,
        dyncamicKeyword: detailsData?.dyncamicKeyword,
        dynamicId: detailsData?.dynamicId,
        tin_certificate_file_link: detailsData.tin_certificate_file_link,
        tin_certificate_issue_date: detailsData.tin_certificate_issue_date
          ? dayjs(detailsData.tin_certificate_issue_date)
          : null,
        tin_certificate_expire_date: detailsData.tin_certificate_expire_date
          ? dayjs(detailsData.tin_certificate_expire_date)
          : null,

        bank_name: detailsData.bank_name,
        bank_account_number: detailsData.bank_account_number,
        branch_name: detailsData.branch_name,
        // wallet_number: detailsData.wallet_number,
        // 👇 Prefill Form.List "supplier" from API dynamicKeys
        can_view_all_products: detailsData.can_view_all_products,
        dynamicKeys: detailsData.dynamicKeys?.map((item) => ({
          dynamic_key_name: item.dynamic_key_name,
          dynamic_id: item.dynamic_id,
          dynamic_keyword: item.dynamic_keyword,
        })),
      });
    }
  }, [detailsData, form]);

  return (
    <SidebarLayout
      title={id ? "Update Business" : "Add New Business"}
      backLink={backLink}
    >
      {open.type === "ADD" && (
        <AssignProductModal open={open.open} handleClose={handleClose} />
      )}
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          // requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
          initialValues={{
            is_published: 1,
            dynamicKeys: [{}],
          }}
        >
          <CustomCard className="grid grid-cols-3 gap-6 p-6  rounded-b-none">
            <div
              className={`pr-6 border-r-2 border-dashed border-light ${
                id ? "col-span-3" : "col-span-2"
              }`}
            >
              <CardTitle>Basic Info</CardTitle>
              <div className="grid grid-cols-2 gap-4 mt-2.5">
                <Form.Item
                  name="logo"
                  label="Business Logo"
                  className="mb-4"
                  valuePropName="fileUrl"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please upload business logo",
                  //   },
                  // ]}
                >
                  <EmployeeFileUpload
                    form={form}
                    accept=".png, .jpg, .jpeg"
                    name="logo"
                    fileSize={5}
                    onChange={(url) =>
                      form.setFieldsValue({
                        logo: url,
                      })
                    }
                  />
                </Form.Item>

                {/* <Form.Item
                name="nid_image_front"
                label="Portal Thumbnail"
                className="mb-4 "
                valuePropName="fileUrl"
                rules={[
                  {
                    required: true,
                    message: "Please upload Portal Thumbnail",
                  },
                ]}
              >
                <EmployeeFileUpload
                  form={form}
                  accept=".png, .jpg, .jpeg"
                  name="nid_image_front"
                  onChange={(url) =>
                    form.setFieldsValue({
                      nid_image_front: url,
                    })
                  }
                />
              </Form.Item> */}

                <Form.Item
                  label="Company Name"
                  name="company_name"
                  rules={[
                    { required: true, message: "Please enter company name" },
                  ]}
                >
                  <Input placeholder="Enter Company name" />
                </Form.Item>

                <Form.Item
                  label="Business Email"
                  name="business_email"
                  validateFirst
                  rules={[
                    {
                      required: true,
                      message: "Please enter email",
                    },
                    {
                      validator: async (_: any, value: string) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        if (!mailRegex.test(value)) {
                          return Promise.reject(
                            new Error("Please enter a valid email")
                          );
                        }

                        if (!isValidTLD(value)) {
                          return Promise.reject(
                            new Error("Please enter a valid TLD")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter business email"
                    disabled={!!id && !isSuperAdmin}
                  />
                </Form.Item>

                <Form.Item
                  label="Mobile Number"
                  name="mobile_number"
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                    {
                      validator: async (_: any, value: string) => {
                        if (!value) {
                          return Promise.reject(
                            new Error("Please enter Mobile Number")
                          );
                        }

                        const phoneRegex = /^(01[3-9])[0-9]{8}$/;
                        if (!phoneRegex.test(value)) {
                          return Promise.reject(
                            new Error(
                              "Please enter a valid Bangladeshi phone number"
                            )
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input prefix={"+88"} size="large" placeholder="XXX XXXX" />
                </Form.Item>

                <Form.Item
                  label="Company Location"
                  name="location"
                  rules={[
                    {
                      required: true,
                      message: "Please enter company location",
                    },
                  ]}
                >
                  <Input placeholder="Enter company location" />
                </Form.Item>
                <Form.Item
                  label="Full Address"
                  name="address"
                  rules={[
                    { required: true, message: "Please enter company address" },
                  ]}
                >
                  <Input placeholder="Enter company address" />
                </Form.Item>

                <Form.Item
                  name="is_published"
                  label="Business Status"
                  rules={[
                    {
                      required: true,
                      message: "Please select business status",
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value={1}>Published</Radio>
                    <Radio value={0}>Unpublished</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="can_view_all_products"
                  label="Show All Product"
                  initialValue={false}
                  rules={[
                    {
                      required: true,
                      message: "Please select the system status",
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>

              <CardTitle className="mt-5 mb-2.5">
                Supplier Keyword Setup
              </CardTitle>
              <Form.List name="dynamicKeys">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div
                        key={key}
                        className="grid grid-cols-2 gap-4 mt-2.5 items-center"
                      >
                        <Form.Item
                          {...restField}
                          label="Supplier Key Name"
                          name={[name, "dynamic_key_name"]}
                        >
                          <Input
                            placeholder="Enter the supplier key name"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Supplier ID"
                          name={[name, "dynamic_id"]}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (value == null || value === "")
                                  return Promise.resolve();

                                const num = Number(value);

                                if (isNaN(num)) {
                                  return Promise.reject(
                                    new Error(
                                      "Supplier ID must be a valid number"
                                    )
                                  );
                                }
                                if (!Number.isInteger(num)) {
                                  return Promise.reject(
                                    new Error("Supplier ID cannot be decimal")
                                  );
                                }
                                if (num < 0) {
                                  return Promise.reject(
                                    new Error("Supplier ID cannot be negative")
                                  );
                                }

                                if (num > 9999999) {
                                  return Promise.reject(
                                    new Error(
                                      "Supplier ID cannot exceed 7 digits"
                                    )
                                  );
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter the supplier ID"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Supplier Keyword"
                          name={[name, "dynamic_keyword"]}
                        >
                          <Input placeholder="Enter the supplier keyword" />
                        </Form.Item>

                        {/* Only show remove button if not the first row */}
                        {index > 0 && (
                          <Button
                            className="action-button mt-5"
                            size="small"
                            icon={<DeleteIcon />}
                            onClick={() => remove(name)}
                          />
                        )}
                      </div>
                    ))}

                    <div className="mt-3 flex justify-start">
                      <Button
                        onClick={() => add()}
                        className="border-none text-[#FE7235]"
                        icon={<PlusOutlined />}
                      >
                        Add
                      </Button>
                    </div>
                  </>
                )}
              </Form.List>

              {/* Documents */}

              <CardTitle className="mt-5">Documents</CardTitle>
              <div className="grid grid-cols-2 gap-4 mt-2.5">
                <Form.Item
                  label="TIN Certificate"
                  name="tin_certificate_file_link"
                  className="mb-4"
                  valuePropName="fileUrl"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please upload business logo",
                  //   },
                  // ]}
                >
                  <EmployeeFileUpload
                    form={form}
                    accept=".png, .jpg, .jpeg, .pdf"
                    name="tin_certificate_file_link"
                    fileSize={10}
                    onChange={(url) =>
                      form.setFieldsValue({
                        tin_certificate_file_link: url,
                      })
                    }
                  />
                </Form.Item>
                <Form.Item
                  label="Issue Date"
                  name="tin_certificate_issue_date"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please TIN issue date",
                  //   },
                  // ]}
                >
                  <DatePicker
                    format={dateFormat}
                    suffixIcon={<DateIcon width={18} height={20} />}
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>
                <Form.Item
                  label="Expire Date"
                  name="tin_certificate_expire_date"
                  dependencies={["tin_certificate_issue_date"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const issueDate = getFieldValue(
                          "tin_certificate_issue_date"
                        );
                        if (!value || !issueDate) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(issueDate, "day")) {
                          return Promise.reject(
                            new Error("Expire date cannot be before issue date")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    format={dateFormat}
                    suffixIcon={<DateIcon width={18} height={20} />}
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>{" "}
                {/* <br />
              <Form.Item
                label="Trade License"
                name="tradeLicense"
                className="mb-4"
                valuePropName="fileUrl"
                rules={[
                  {
                    required: true,
                    message: "Please upload business logo",
                  },
                ]}
              >
                <EmployeeFileUpload
                  form={form}
                  accept=".png, .jpg, .jpeg"
                  name="nid_image_front"
                  onChange={(url) =>
                    form.setFieldsValue({
                      nid_image_front: url,
                    })
                  }
                />
              </Form.Item>
              <Form.Item label="Issue Date" name="tradeIssueDate">
                <DatePicker
                  format={dateFormat}
                  suffixIcon={<DateIcon width={18} height={20} />}
                  style={{ width: "100%" }}
                  size="large"
                />
              </Form.Item>
              <Form.Item label="Expire Date" name="tradeExpireDate">
                <DatePicker
                  format={dateFormat}
                  suffixIcon={<DateIcon width={18} height={20} />}
                  style={{ width: "100%" }}
                  size="large"
                />
              </Form.Item> */}
              </div>

              {/* Bank Details */}
              <CardTitle className="mt-5">Bank Details</CardTitle>
              <div className="grid grid-cols-2 gap-4 mt-2.5">
                <Form.Item
                  label="Bank Name"
                  name="bank_name"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please bank name",
                  //   },
                  // ]}
                >
                  <Input placeholder="Enter bank name" />
                </Form.Item>

                <Form.Item
                  label="Account Number"
                  name="bank_account_number"
                  rules={[
                    // {
                    //   required: true,
                    //   message: "Please enter the account number",
                    // },
                    {
                      pattern: /^\d+$/,
                      message: "Account number must contain only numbers",
                    },
                  ]}
                >
                  <Input placeholder="12345XXXXXXXX2345" />
                </Form.Item>

                <Form.Item
                  label="Branch Name"
                  name="branch_name"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please branch name",
                  //   },
                  // ]}
                >
                  <Input placeholder="Enter bank branch" />
                </Form.Item>
                {/* <Form.Item
                  label="Wallet Number"
                  name="wallet_number"
                  rules={[
                    {
                      required: true,
                      message: "Please wallet number",
                    },
                  ]}
                >
                  <Input placeholder="12345XXXXXXXX2345" />
                </Form.Item> */}
              </div>
            </div>
            {!id && (
              <div className="col-span-1">
                {/* <div className="mb-4">
                  <CardTitle
                    rightSide={
                      <Button
                        className="outline-button-primary !bg-transparent border-none font-medium !p-0 !h-auto"
                        icon={<AddIcon />}
                        // onClick={() => handleOpen({ type: "ADD" })}
                      >
                        Add
                      </Button>
                    }
                  >
                    Products
                  </CardTitle>

                  <div className="py-4 mt-8 flex justify-center">
                    <DocumentIcon />
                  </div>
                </div>

                <div className="mb-4">
                  <CardTitle>Logs</CardTitle>

                  <div className="py-4 mt-8 flex justify-center">
                    <DocumentIcon />
                  </div>
                </div> */}
              </div>
            )}
          </CustomCard>
          {/* sticky bottom-0 */}
          <div className="p-6 rounded-md rounded-t-none bg-white border border-t-0 border-light">
            <Space className="w-full mt-2" size={12}>
              <Link to={backLink}>
                <Button
                  className="normal-dark-button modal-button"
                  size="middle"
                >
                  Cancel
                </Button>
              </Link>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isLoading || isUpdateLoading}
              >
                {id ? "Update" : "Submit"}
              </Button>
            </Space>
          </div>
        </Form>
      )}
    </SidebarLayout>
  );
}
